<template>
  <client-only>
    <main role="main">
      <GridContainer
        v-if="cartInitialized"
        class="cart-page"
      >
        <OrderSteps
          step="cart"
          :has-no-payment="isFreeOfChargeCart"
        />

        <Heading
          class="order-title"
          align="center"
          size="xl"
          spaces-bottom
          data-testid="cart-quantity"
        >
          {{ cartQuantity }} Artikel im Warenkorb
        </Heading>

        <div
          v-if="cartIsEmpty"
          class="text--center"
        >
          <p>Dein Warenkorb ist leer.</p>
          <br />
          <LinkButton
            data-tealium-rel="content.text.weiter-einkaufen"
            to="/"
            tag="nuxt-link"
          >
            Weiter einkaufen
          </LinkButton>
        </div>

        <GridRow
          v-else
          align-h="center"
        >
          <GridColumn :md="10">
            <OrderTable>
              <div
                slot="footer"
                class="order-table__row cart-page__order-table-footer"
              >
                <div
                  v-if="isPaypalExpressVisibleInCart"
                  class="cart-page__payment cart-page__payment-express"
                >
                  <span class="cart-page__divider cart-page__divider--first">
                    <span>Express Checkout</span>
                  </span>
                  <PaypalExpressCheckoutButton
                    v-if="isPaypalExpressVisibleInCart"
                    class="cart-page__paypal-button"
                  />
                  <span class="cart-page__divider"><span>oder</span></span>
                </div>
                <div
                  v-else
                  class="cart-page__payment cart-page__payment-spacer"
                />
                <div class="cart-page__payment cart-page__payment-regular">
                  <LinkButton
                    v-if="checkoutInitUrlSuffix"
                    :to="`/shop/${checkoutInitUrlSuffix}`"
                    primary
                    tag="nuxt-link"
                    data-testid="checkout-button"
                    class="cart-page__buy-button"
                  >
                    Zur Kasse gehen
                  </LinkButton>

                  <LinkButton
                    to="/"
                    text
                    tag="nuxt-link"
                    data-tealium-rel="content.text.weiter-einkaufen"
                  >
                    Weiter einkaufen
                  </LinkButton>
                </div>
              </div>
            </OrderTable>
            <ShareCart />
          </GridColumn>
        </GridRow>
      </GridContainer>

      <div
        v-else
        class="cart-page cart-page--loading"
      >
        <div class="text text--center">Der Warenkorb wird geladen…</div>
      </div>
    </main>
  </client-only>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { castArray, isEmpty } from 'lodash-es';
import { mapActions, mapGetters } from 'vuex';
import type { FetchError } from 'ofetch';
import type { BackendCouponInfoResponse, BackendResponse } from '@/lib/shop-api';
import type { AddToCart } from '@/store/cart';
import type { Item } from '@/lib/types/cartdata';

import serializeCartForTealium from '@/lib/tealium/serializers/cart';

import OrderSteps from '@/components/order-steps.vue';
import OrderTable from '@/components/order-table.vue';
import PaypalExpressCheckoutButton from '@/components/paypal-express-checkout-button.vue';
import {
  GridColumn,
  GridContainer,
  GridRow,
  LinkButton,
  Heading,
} from '@i22-td-smarthome/component-library';
import { handleCartErrors } from '@/helpers/add-to-cart';
import ShareCart from '@/components/share-cart.vue';
import { TrackAsync } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';

export default defineComponent({
  layout: 'default',
  name: 'Warenkorb',
  setup() {
    getHeader({
      title: ref('Warenkorb'),
      isIndexed: ref(false),
    });
  },
  components: {
    ShareCart,
    LinkButton,
    GridContainer,
    GridColumn,
    GridRow,
    Heading,
    OrderSteps,
    OrderTable,
    PaypalExpressCheckoutButton,
  },
  async mounted() {
    // When articleNumbers and/or contractNumbers are given as query parameters
    // we add those products to the cart.
    const items: AddToCart[] = [];
    this.addedArticleNumbers.forEach(({ articleNumber, quantity }) => {
      items.push({ articleNumber, quantity });
    });

    // @ts-ignore
    await this.fetchCart();

    let errorToShow: FetchError | undefined;
    if (items.length > 0) {
      try {
        // @ts-ignore
        await this.addCartItems(items);
      } catch (error: FetchError<BackendResponse> | any) {
        errorToShow = error;
      }
    }

    if (this.addedCouponCode) {
      try {
        // @ts-ignore
        await this.cartAddCoupon({ redeemedCoupon: this.addedCouponCode });
      } catch (error: FetchError<BackendCouponInfoResponse> | any) {
        // addToCart & cartAddCoupon contains BE error.
        if (error?.data?.errors && errorToShow?.data?.errors) {
          errorToShow.data.errors = {
            ...errorToShow.data?.errors,
            ...error.data?.errors,
          };
        }
        // cartAddCoupon contains a be error, addToCart a server error
        else if (error?.data?.errors) {
          errorToShow = error;
        }
        // addToCart contains a BE error, cartAddCoupon a server error
        else if (errorToShow?.data?.errors) {
          // do nothing
        } else {
          errorToShow = error;
        }
      }
    }

    TrackAsync();

    await this.$router.replace({ query: null } as any).catch(() => {});
    // raise errors after route change - it will close the modal!
    if (!isEmpty(errorToShow)) {
      await handleCartErrors(errorToShow, (articleNumber) => {
        // @ts-ignore
        const item = this.cartItems.find((e: Item) => e.articleNumber === articleNumber);
        if (item) return item;
        return item?.name || articleNumber;
      });
    }
  },
  computed: {
    ...mapGetters('cart', [
      'cartInitialized',
      'cartIsEmpty',
      'cartItems',
      'cartQuantity',
      'checkoutInitUrlSuffix',
      'isMixedCart',
      'wasPaypalExpressReceived',
      'cartAmountReductionsIncluded',
      'possiblePaymentTypes',
      'isFreeOfChargeCart',
    ]),
    addedCouponCode() {
      return this.$route.query.couponCode || null;
    },
    addedArticleNumbers() {
      const articleNumbers = castArray(this.$route.query['articleNumbers[]'] || []) as string[];

      // Count articleNumbers and set the quantity
      return articleNumbers.reduce((acc: AddToCart[], articleNumber: string) => {
        const existingArticle: AddToCart | undefined = acc.find(
          (item) => item.articleNumber === articleNumber
        );

        if (existingArticle) {
          existingArticle.quantity += 1;
        } else {
          acc.push({ articleNumber, quantity: 1 });
        }

        return acc;
      }, []);
    },
    addedContractNumbers() {
      return castArray(this.$route.query['contractNumbers[]'] || []);
    },
    additionalTealiumData() {
      return serializeCartForTealium('add', this.cartItems);
    },
    isPaypalExpressVisibleInCart() {
      return (
        !this.wasPaypalExpressReceived &&
        !this.isMixedCart &&
        this.cartAmountReductionsIncluded > 0 &&
        // @ts-ignore
        this.possiblePaymentTypes.includes('computop_paypal_express')
      );
    },
  },
  methods: {
    ...mapActions('cart', ['fetchCart', 'addCartItems', 'cartAddCoupon']),
  },
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
.cart-page {
  padding-bottom: 48px;
  &--loading {
    padding-bottom: 48px;
  }

  &__order-table-footer {
    border-bottom: none;
  }

  &__buy {
    &-button {
      @include mq(false, $mq-md - 1) {
        width: 100%;
      }
    }
  }

  &__paypal-button {
    margin-bottom: $grid-base;
    width: 100%;
    @include mq($mq-md) {
      width: auto;
    }
    @include mq($mq-md) {
      margin-bottom: 0;
    }
  }

  &__payment {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    border-bottom: none;
    justify-content: center;
    grid-gap: $grid-base;
    align-items: flex-start;

    @include mq($mq-md) {
      grid-gap: $grid-base;
    }

    &-express {
    }

    &-regular {
      margin-bottom: $grid-base * 2;
      margin-top: $grid-base * 2;
      flex-direction: column;
      align-items: center;
      grid-gap: $grid-base * 2;
    }

    &-spacer {
      border-bottom: 1px solid var(--color-stroke-subtle);
      margin-top: -1 * $grid-base;
      margin-bottom: $grid-base * 3;
    }
  }

  &__divider {
    flex: 0 0 100%;
    color: var(--color-font-subtle);
    text-align: center;
    font-weight: 700;
    padding-top: $grid-base;
    padding-bottom: $grid-base * 3;

    &--first {
      padding-top: 0;
    }

    &:after {
      content: '';
      width: 100%;
      border-bottom: 1px solid var(--color-stroke-subtle);
      display: block;
      margin-block: -1 * $grid-base;
    }

    span {
      padding-inline: $grid-base;
      background: var(--color-bg);
    }
  }
}
</style>
